import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { v4 as uuid } from 'uuid';
import gql from 'graphql-tag';
import { useMutation, useApolloClient } from '@apollo/client';
import { useI18n, useLogger, useSelector } from '~Hooks';
import {
  createSwishPayment,
  createSwishPaymentVariables,
  getPayment,
  getPaymentVariables,
} from './__queries__';
import { PAYMENT_STATUS } from '~../__queries__';

import { MethodProps } from '../types';
import { PrintRow } from '~Components/PrinterManager';

const PAYMENT_QUERY = gql`
  query getPayment($id: ID!) {
    payment: allPayments(filter: { id: $id }) {
      id
      status
    }
  }
`;

const CREATE_SWISH_PAYMENT = gql`
  mutation createSwishPayment($amount: Float!, $venueId: String!, $companyId: String) {
    createSwishPayment(
      amount: $amount
      venueId: $venueId
      companyId: $companyId
      currency: "SEK"
    ) {
      success
      error
      message
      token
      paymentId
    }
  }
`;

const SwishQR: React.FunctionComponent<MethodProps> = ({
  payment,
  onComplete,
  onFail,
  onMetaChange,
  amount,
}) => {
  const { paymentId } = payment.meta;
  const location = useLocation();
  const client = useApolloClient();
  const [, , venueId] = location.pathname.split('/');
  const companyId = useSelector((state) => state.company);
  const logger = useLogger('payment:swish');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const { i18n } = useI18n();

  const [createSwishPayment] = useMutation<createSwishPayment, createSwishPaymentVariables>(
    CREATE_SWISH_PAYMENT,
  );

  const query = client.watchQuery<getPayment, getPaymentVariables>({
    query: PAYMENT_QUERY,
    fetchPolicy: 'no-cache',
    variables: {
      id: paymentId!,
    },
  });

  const checkPaymentStatus = () => {
    query
      .refetch()
      .then((res) => {
        const paymentData = res.data.payment && res.data.payment[0];

        if (!paymentData) {
          logger.error('No payment data, retrying query...', { errorResponse: res });
          timer.current = setTimeout(() => {
            checkPaymentStatus();
          }, 1500);

          return;
        }

        if (paymentData.status === PAYMENT_STATUS.PENDING) {
          timer.current = setTimeout(() => {
            checkPaymentStatus();
          }, 1500);

          return;
        }

        if (paymentData.status === PAYMENT_STATUS.SUCCESS) {
          logger.info('SwishQR payment successful', { paymentData });
          onComplete(null, {
            transactionId: uuid(),
          });
        }

        if (paymentData.status === PAYMENT_STATUS.FAILED) {
          logger.info('SwishQR payment failed', { paymentData });
          onFail();
        }
      })
      .catch(() => {
        // Most likely a network error, retrying
        timer.current = setTimeout(() => {
          checkPaymentStatus();
        }, 1500);
      });
  };

  useEffect(() => {
    const run = async () => {
      const { data } = await createSwishPayment({
        variables: {
          amount: amount!,
          venueId,
          companyId,
        },
      });

      if (data?.createSwishPayment.success) {
        onMetaChange({
          paymentId: data.createSwishPayment.paymentId,
          qr: `D${data.createSwishPayment.token}`,
          amount,
        });

        const message: PrintRow[] = [
          { content: '     Swish', fontSize: 28, alignment: 'CENTER' },
          { content: ' ' },
          { content: ' ' + i18n('Swish.ScanCode'), fontSize: 18, alignment: 'CENTER' },
          { content: ' ' },
          { content: `D${data.createSwishPayment.token}`, qr: true },
          { content: ' ' },
          {
            content: `    ${i18n('Swish.ToPay')}: ${amount! / 100}:-`,
            fontSize: 18,
            alignment: 'CENTER',
          },
        ];

        window.printerManager?.initiatePrint({
          data: message,
          printType: 'SWISH',
        });
      } else {
        onFail(data?.createSwishPayment.message);
      }
    };

    try {
      run();
    } catch (error) {
      // @ts-ignore
      onFail(error.message);
    }
  }, []);

  useEffect(() => {
    if (!paymentId) {
      // Do nothing
      return;
    }

    checkPaymentStatus();

    return () => {
      clearTimeout(timer.current);
    };
  }, [paymentId]);

  useEffect(() => {
    if (payment.meta.cancelRequested) {
      logger.info('SwishQR payment cancelled', { paymentId });
      clearTimeout(timer.current);
      onFail('Cancelled');
    }
  }, [payment.meta.cancelRequested]);

  return null;
};

export default SwishQR;
